import type { GameId } from '@redd/contracts';

export type GameBuyIntent = { owner: string; game: GameId; name: string; key: string; priceFen: number; quantity: number };
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
export function gameBuyIntentKey(owner: string, game: GameId, name: string) {
  return `redd:web:buy-order:v1:${encodeURIComponent(owner)}:${game}:${encodeURIComponent(name)}`;
}
export function readGameBuyIntent(storage: Storage, owner: string, game: GameId, name: string): GameBuyIntent | undefined {
  const raw = storage.getItem(gameBuyIntentKey(owner, game, name));
  if (!raw) return undefined;
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || !parsed) throw new Error('pending_buy_order_invalid');
  const value = parsed as Partial<GameBuyIntent>;
  if (value.owner !== owner || value.game !== game || value.name !== name || typeof value.key !== 'string' || value.key.length < 8 || value.key.length > 100 || !Number.isSafeInteger(value.priceFen) || value.priceFen! < 2 || !Number.isInteger(value.quantity) || value.quantity! < 1 || value.quantity! > 100) throw new Error('pending_buy_order_invalid');
  return value as GameBuyIntent;
}
export function saveGameBuyIntent(storage: Storage, intent: GameBuyIntent) {
  const previous = readGameBuyIntent(storage, intent.owner, intent.game, intent.name);
  if (previous && (previous.key !== intent.key || previous.priceFen !== intent.priceFen || previous.quantity !== intent.quantity)) throw new Error('pending_buy_order_exists');
  storage.setItem(gameBuyIntentKey(intent.owner, intent.game, intent.name), JSON.stringify(intent));
}
export function clearGameBuyIntent(storage: Storage, intent: GameBuyIntent) {
  const previous = readGameBuyIntent(storage, intent.owner, intent.game, intent.name);
  if (previous?.key === intent.key) storage.removeItem(gameBuyIntentKey(intent.owner, intent.game, intent.name));
}
export function resolveGameBuyIntent(visible: GameBuyIntent | undefined, saved: GameBuyIntent | undefined, fresh: GameBuyIntent): GameBuyIntent {
  for (const value of [visible, saved]) {
    if (value && (value.owner !== fresh.owner || value.game !== fresh.game || value.name !== fresh.name)) throw new Error('pending_buy_order_conflict');
  }
  if (visible && saved && (visible.key !== saved.key || visible.priceFen !== saved.priceFen || visible.quantity !== saved.quantity)) throw new Error('pending_buy_order_conflict');
  return visible ?? saved ?? fresh;
}
export async function withGameBuyLock<T>(locks: LockManager | undefined, owner: string, game: GameId, name: string, run: () => Promise<T>): Promise<T> {
  if (!locks) throw new Error('buy_order_lock_unavailable');
  return locks.request(gameBuyIntentKey(owner, game, name), { mode: 'exclusive', ifAvailable: true }, async lock => {
    if (!lock) throw new Error('buy_order_locked');
    return run();
  });
}
